import Result from 'true-myth/result'

import type { LocalStorageTokenData } from '../types'
import type { ClientWapiError, FederatedSession } from './wapi-client'

export interface SessionRestoreDeps {
    /** The raw mirror entry (incl. an expired token), or null when absent/malformed. */
    mirror: LocalStorageTokenData | null
    /** Refresh an expired cognito token (refreshRequest bound to urlRoot). */
    refresh: (opts: {
        email: string
        token: string
        sessionId?: string
    }) => Promise<Result<FederatedSession, ClientWapiError>>
    writeMirror: (entry: LocalStorageTokenData) => void
    setSnowplowUserId: (id: string | null) => void
    setRumUser: (user: { id?: string; email?: string }) => void
}

/**
 * Silently restore a session from the mirror by **refreshing an expired cognito token**
 * (ui-react `cognitoAutoLogIn`/`refreshToken` parity). Only cognito sessions can be
 * refreshed — a federated (Google) session re-restores via its SDK, so this returns
 * `Ok(false)` for those and lets the caller fall through. On success the refreshed mirror
 * is written and the analytics identity is set.
 *
 * "Nothing to refresh" is `Ok(false)` and "the refresh failed" is an `Err`. A caller that
 * only picks the next step treats them alike; the split exists so one that reports on a
 * silent subsystem can tell a visitor with no cognito session from a refresh endpoint
 * failing for everyone.
 */
export async function attemptSessionRestore(
    deps: SessionRestoreDeps,
): Promise<Result<boolean, ClientWapiError>> {
    const mirror = deps.mirror
    if (mirror?.provider !== 'cognito' || !mirror.email || !mirror.authToken) {
        return Result.ok(false)
    }

    const refreshed = await deps.refresh({
        email: mirror.email,
        token: mirror.authToken,
        sessionId: mirror.sessionId || undefined,
    })
    if (refreshed.isErr) {
        return Result.err(refreshed.error)
    }
    const session = refreshed.value

    deps.writeMirror({
        authToken: session.token,
        authTokenExp: session.expiresAt,
        email: mirror.email,
        identityId: session.identityId,
        provider: 'cognito',
        sessionId: session.sessionId,
    })
    deps.setSnowplowUserId(session.identityId)
    deps.setRumUser({
        ...(session.identityId ? { id: session.identityId } : {}),
        ...(mirror.email ? { email: mirror.email } : {}),
    })

    return Result.ok(true)
}
